import React, {useEffect, useRef, useState} from "react";
import { useTranslation } from "react-i18next";

import ButtonAction from "./buttons/ButtonAction";
import {AddIcon, PersonChipBlack, SearchIcon, } from "../components/buttons/IconButtons";
import CustomMediumChip from "./CustomMediumChip";
import { POSITION_MODEL } from "../Helpers/PositionModel";
import {DEBOUNCE_DELAY, filterByLabelEmailPhone} from "../Apis/functions";
import {debounce} from 'lodash';
import AddChipAutoComplete from "./CustomAddChip/AddChipAutoComplete";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * @Description CustomAddChip allows selecting items from a list using an autocomplete input,
 *              adding them to a selected list, and rendering them as removable chips.
 *
 * @param {string} autocompleteId - Unique ID for the autocomplete input.
 * @param {string} buttonId - Unique ID for the add button.
 * @param {boolean} [emptyFields] - Whether the input should be flagged as empty (validation).
 * @param {boolean} [disabled] - Disables the autocomplete, button, and chips if true.
 * @param {string} label - Key to be translated as the input label.
 * @param {boolean} [required] - Whether the field is required.
 * @param {Array<{ id: string, label: string, subSection?: function }>} data - List of selectable options.
 * @param {Array<{ id: string, label: string }>} selectedList - Items currently selected and rendered as chips.
 * @param {function} setSelectedList - Callback to update the selected items list.
 * @param {boolean} [cleanChipSearch=false] - If true, clears the current selection in the autocomplete input.
 * @param {function} [setCleanChipSearch] - Callback to reset the `cleanChipSearch` flag to false after clearing.
 * @param {boolean} [noOptionsCard=false] - Hides the default "no options" card in autocomplete dropdown.
 * @param {boolean} [showRenderOption=false] - Enables custom rendering for options in the dropdown.
 * @param {React.ElementType} [chipIcon=PersonChipBlack] - Custom icon component for each chip.
 * @param {function} [onDeleteItem=null] - Callback executed when a chip is deleted.
 * @param {string} [helperText=''] - Helper/legend text displayed under the input field.
 * @param {function} [filterOptions=filterByLabelEmailPhone] - Custom filtering function for autocomplete options.
 * @param {number|string} [maxLength=undefined] - Maximum number of items allowed in `selectedList`.
 * @param {boolean} [hideButton=false] - If true, hides the "Add" button and automatically adds items on selection
 * @param classNameCustomCards - New class for a box containing custom medium chips
 * @param open - Optional prop to control dropdown open state externally.
 * @param setOpen - Callback to update the `open` state from parent.
 * @param searchApi - Optional function to trigger search when clicking search icon.
 * @param isFetchOnSearch - Indicates whether it is a search when typing in the input
 * @param extraClassNameContainer - Optional new class to container
 * @param {boolean} [reset] - When set to `true`, clears the input value.
 * @param {boolean} [onlyOnKeyDown] - If `true`, prevents automatic search while typing and triggers search only on key events.
 * @param {boolean} [completeWidth=false] - Forces each chip to stretch and use the full available width.
 * @param {JSX.Element|null} [rightContent=null] - Optional content (e.g., status badge or icon) shown at the end.
 * @param String placeholder: Defines the placeholder text for the input field (optional)


 * @returns {JSX.Element} The rendered component.
 */

export const CustomAddChip = ({
                                    autocompleteId,
                                    buttonId,
                                    emptyFields,
                                    disabled,
                                    label,
                                    required,
                                    data = [],
                                    selectedList = [],
                                    setSelectedList,
                                    cleanChipSearch = false,
                                    setCleanChipSearch = () => { },
                                    noOptionsCard = false,
                                    showRenderOption = false,
                                    chipIcon,
                                    onDeleteItem = null,
                                    helperText = '',
                                    filterOptions = filterByLabelEmailPhone,
                                    maxLength = undefined,
                                    hideButton = false,
                                    classNameCustomCards='',
                                    open,
                                    setOpen,
                                    searchApi,
                                    isFetchOnSearch=false,
                                    extraClassNameContainer='',
                                    reset,
                                    onlyOnKeyDown,
                                    completeWidth=false,
                                    placeholder,
                                    positionIconOptions
                              }) => {


    const { t } = useTranslation();
    const isTyping = useRef(true);
    const [itemSelected, setItemSelected] = useState(null);
    const [inputValue, setInputValue] = useState("");
    let optionalProps = {}

    const isDisabledInput = disabled || (maxLength !== undefined && selectedList.length >= parseInt(maxLength));
    const isDisabledButton = disabled || !(itemSelected && itemSelected.id);
    const isDisabledChip = disabled;

    if(searchApi){
        optionalProps = { ...optionalProps, searchIcon: <SearchIcon onClick={() => searchApi(inputValue)}/>, clearIcon: null };
    }

    useEffect(() => {
        if(onlyOnKeyDown) return;
        if (!isTyping.current) return;
        const debouncedFilter = debounce(() => {
            if(isFetchOnSearch && searchApi){
                searchApi(inputValue)
            }
        }, DEBOUNCE_DELAY);
        debouncedFilter();
        return () => debouncedFilter.cancel();
    }, [inputValue]);

    // Removes an item from the selected list
    const handleDelete = (element) => {
        if (onDeleteItem) onDeleteItem(element);
        const updatedList = (selectedList || []).filter(e => e.id !== element.id);
        setSelectedList(updatedList);
    };

    const handleAdd = (itemArg) => {
        const possibleEvent = itemArg && typeof itemArg === 'object' && typeof itemArg.preventDefault === 'function';
        const toAdd = !possibleEvent && itemArg && itemArg.id ? itemArg : (itemSelected && itemSelected.id ? itemSelected : null);

        if (!toAdd) return;
        const updatedList = [...(selectedList || []), toAdd];
        setSelectedList(updatedList);
        setItemSelected(null);
        setInputValue("");
    };

    useEffect(() => {
        if (cleanChipSearch) {
            setItemSelected(null);
            setInputValue("");
            // reset flag in parent (if function)
            try { setCleanChipSearch(false); } catch (err) { /* no-op */ }
        }
    }, [cleanChipSearch, setCleanChipSearch]);

    return (
        <>
            {/* Autocomplete search input and Add button */}
            <div className={'d-flex gap-3 ' + extraClassNameContainer}>
                <div className='flex-grow-1'>
                    <AddChipAutoComplete
                        id={autocompleteId}
                        helperText={helperText}
                        data={data.filter(e => !(selectedList || []).map(s => s.id).includes(e.id))} // Filter out already selected items
                        value={itemSelected?.label || (reset ? "" : inputValue) || ''}
                        setValue={(e) => {
                            isTyping.current = false;
                            // if AutoCompleteSearch sends the entire object when selecting,
                            // the state is updated here; we don't call handleAdd from setValue
                            setItemSelected(e && e.id ? e : e);
                            setTimeout(() => { isTyping.current = true }, 500);
                        }}
                        label={t(label)}
                        required={required}
                        emptyFields={emptyFields}
                        disabled={isDisabledInput}
                        onInputChange={(event, newInputValue) => {
                            isTyping.current = true;
                            setInputValue(newInputValue);
                        }}
                        handleFn={(e) => {
                            // If hideButton -> when you select option, add directly
                            if (hideButton && e && e.id) {
                                handleAdd(e);
                                return;
                            }
                            // if not, just update the state
                            setItemSelected(e);
                        }}
                        reset={reset}
                        onKeyDownEnter={() => {
                            if (!hideButton) handleAdd();
                            else if(searchApi) searchApi(inputValue)
                        }}
                        noOptionsCard={noOptionsCard}
                        showRenderOption={showRenderOption}
                        filterOptions={filterOptions}
                        inputValue={inputValue}
                        selectedList={selectedList}
                        itemSelected={itemSelected}
                        open={open}
                        setOpen={setOpen}
                        {...optionalProps}
                        placeholder={placeholder}
                        positionIconOptions={positionIconOptions}
                    />
                </div>

                {/* Button to add selected item */}
                {!hideButton && (
                    <ButtonAction
                        id={buttonId}
                        name='add'
                        className='btn-secondary-600'
                        Icon={AddIcon}
                        splitName={false}
                        onClick={() => handleAdd()}
                        disabled={isDisabledButton}
                    />
                )}
            </div>

            {/* Render selected items as chips with delete option */}
            {Array.isArray(selectedList) && selectedList.length > 0 && (
                    <div className={`${classNameCustomCards} d-flex gap-4 input-padding-bottom flex-wrap mt-3`}>
                        {selectedList.map((element) => (
                            <CustomMediumChip
                                key={element.id}
                                title={element.label}
                                completeWidth={completeWidth}
                                disabled={isDisabledChip}
                                showAvatar={!!(element?.Icon || chipIcon)}
                                Icon={element?.Icon || chipIcon}
                                iconPosition={POSITION_MODEL.LEFT}
                                onClickClose={() => handleDelete(element)}
                            >
                                {element?.subSection?.({divider:false}) && (
                                    <div className='mt-2 d-flex w-100 align-items-center align-content-center justify-content-between'>
                                        {element?.subSection?.({ divider:false})}
                                    </div>
                                )}
                            </CustomMediumChip>
                        ))}
                    </div>
                )}
        </>
    );
};
